import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Button,
  Grid,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Stack,
  Paper,
  Divider,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { DARK_MODE, PRINTER_ERROR, PRINTER_LOADING, ZPL_TEMPLATE_FOR_BARCODE } from '../../constants';
import { axiosInstance } from '../../api/api';
import { useMessageContextManager } from '../MessageOverlay/MessageOverlay';
import { useResolutionScale } from '../../hooks/useResolutionScale';
import { BasicTable, LargeButton, ScanOrEnterBarcodeForm } from '..';
import { formatZplTemplate } from '../../utils/utils';

const DEFAULT_COLUMNS = [
  {
    width: 250,
    title: 'Serial Number (VIN)',
    field: 'serialNumber',
    align: 'center',
  },
];

const PRINT_LAYOUT_CACHE_KEY = 'barcodePrintLayoutConfigV1';
const PRINT_LAYOUT_PRESETS_KEY = 'barcodePrintLayoutPresetsV1';

const DEFAULT_CUSTOM_PRINT_CONFIG = {
  leftMargin: 0,
  topMargin: 0,
  horizontalGap: 140,
  verticalGap: 160,
  copies: 1,
  arrangement: 'ROW',
  maxColumns: 2,
  maxRows: 2,
  mode: 'BUILDER',
  valueTransform: 'NONE',
  showDate: true,
  showTime: true,
  showPartText: true,
  showQr: true,
  dateX: 150,
  dateY: 50,
  timeX: 600,
  timeY: 50,
  partX: 150,
  partY: 130,
  qrX: 150,
  qrY: 200,
  dateFontH: 50,
  dateFontW: 60,
  partFontH: 75,
  partFontW: 45,
  qrMagnification: 9,
  labelWidth: 900,
  customTextPrefix: '',
  rawTemplate: `^XA
^FO150,50^A0N,50,60^FD{Date} {Time}^FS
^FO150,130^A0N,75,45^FD{PartNumber}^FS
^FO150,200^BQN,2,9^FDLA,{PartNumber}^FS
^XZ`,
};

const getCachedCustomPrintConfig = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PRINT_LAYOUT_CACHE_KEY) || '{}');
    return {
      ...DEFAULT_CUSTOM_PRINT_CONFIG,
      ...parsed,
    };
  } catch (_err) {
    return DEFAULT_CUSTOM_PRINT_CONFIG;
  }
};

const sanitizeNumber = (value, fallback = 0, min = 0, max = 2000) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
};

const getCachedPrintPresets = () => {
  try {
    return JSON.parse(localStorage.getItem(PRINT_LAYOUT_PRESETS_KEY) || '[]');
  } catch (_err) {
    return [];
  }
};

const getDateTimeParts = (now = new Date()) => {
  return {
    date: now.toISOString().slice(0, 10),
    time: now.toTimeString().slice(0, 5),
  };
};

const buildLabelBodyZpl = (barcodeValue, config, dateTime) => {
  const { date, time } = dateTime || getDateTimeParts();
  let transformedValue = barcodeValue || '';

  if (config.valueTransform === 'UPPER') transformedValue = transformedValue.toUpperCase();
  if (config.valueTransform === 'LOWER') transformedValue = transformedValue.toLowerCase();

  const partValue = `${config.customTextPrefix || ''}${transformedValue}`;

  if (config.mode === 'RAW_TEMPLATE') {
    return (config.rawTemplate || '')
      .replace(/{PartNumber}/g, partValue)
      .replace(/{VIN}/g, partValue)
      .replace(/{Date}/g, date)
      .replace(/{Time}/g, time)
      .replace('^XA', '')
      .replace('^XZ', '')
      .trim();
  }

  const lines = [];
  if (config.showDate) {
    lines.push(
      `^FO${sanitizeNumber(config.dateX, 150)},${sanitizeNumber(config.dateY, 50)}^A0N,${sanitizeNumber(config.dateFontH, 50, 10, 400)},${sanitizeNumber(config.dateFontW, 60, 10, 400)}^FD${date}^FS`,
    );
  }
  if (config.showTime) {
    lines.push(
      `^FO${sanitizeNumber(config.timeX, 600)},${sanitizeNumber(config.timeY, 50)}^A0N,${sanitizeNumber(config.dateFontH, 50, 10, 400)},${sanitizeNumber(config.dateFontW, 60, 10, 400)}^FD${time}^FS`,
    );
  }
  if (config.showPartText) {
    lines.push(
      `^FO${sanitizeNumber(config.partX, 150)},${sanitizeNumber(config.partY, 130)}^A0N,${sanitizeNumber(config.partFontH, 75, 10, 400)},${sanitizeNumber(config.partFontW, 45, 10, 400)}^FD${partValue}^FS`,
    );
  }
  if (config.showQr) {
    lines.push(
      `^FO${sanitizeNumber(config.qrX, 150)},${sanitizeNumber(config.qrY, 200)}^BQN,2,${sanitizeNumber(config.qrMagnification, 9, 1, 20)}^FDLA,${partValue}^FS`,
    );
  }

  return lines.join('\n');
};

const buildCustomizedZpl = (barcodeValue, customConfig, dateTime) => {
  const {
    leftMargin,
    topMargin,
    horizontalGap,
    verticalGap,
    copies,
    arrangement,
    maxColumns,
    maxRows,
  } = customConfig;
  const labelBody = buildLabelBodyZpl(barcodeValue, customConfig, dateTime);

  const totalCopies = sanitizeNumber(copies, 1, 1, 50);
  const cols = sanitizeNumber(maxColumns, 2, 1, 10);
  const rows = sanitizeNumber(maxRows, 2, 1, 10);
  const xGap = sanitizeNumber(horizontalGap, 140, 0, 2000);
  const yGap = sanitizeNumber(verticalGap, 160, 0, 2000);
  const xMargin = sanitizeNumber(leftMargin, 0, 0, 2000);
  const yMargin = sanitizeNumber(topMargin, 0, 0, 2000);

  const blocks = Array.from({ length: totalCopies }).map((_, index) => {
    let rowIndex = 0;
    let colIndex = 0;

    if (arrangement === 'COLUMN') {
      rowIndex = index % rows;
      colIndex = Math.floor(index / rows);
    } else {
      colIndex = index % cols;
      rowIndex = Math.floor(index / cols);
    }

    const shiftX = xMargin + (colIndex * xGap);
    const shiftY = yMargin + (rowIndex * yGap);

    return `^XA
^PW${sanitizeNumber(customConfig.labelWidth, 900, 200, 4000)}
^LH${shiftX},${shiftY}
${labelBody}
^XZ`;
  });

  return blocks.join('\n');
};

const UpdateSerialNo = ({ selectedRows, handleClose }) => {
  const handleSubmit = () => {
    if (selectedRows && selectedRows.length > 0) {
      const activeRow = selectedRows[0];
      let zplString = '';

      // Check if backend provided a ZPL label
      if (activeRow.labels && activeRow.labels.length > 0 && activeRow.labels[0].value) {
        zplString = activeRow.labels[0].value;
      } else {
        // Fallback: Create the ZPL properly using the template
        zplString = formatZplTemplate(ZPL_TEMPLATE_FOR_BARCODE, activeRow.partNumber || activeRow.serialNumber || '');
      }

      const zplEncodedString = encodeURIComponent(zplString);
      window.location.href = `nirmaan://?cmd=print&zplstring=${zplEncodedString}`;

      setTimeout(() => {
        handleClose();
      }, 3000);
    }
  };

  return (
    <Grid
      container
      paddingX={3}
      paddingTop={2}
      paddingBottom={2}
      alignItems="center"
      gap={2}
      justifyContent="center"
    >
      <LargeButton
        backgroundColor="#0F107B"
        name="CANCEL"
        handleButtonClick={handleClose}
        test-id="CANCEL"
      />
      <LargeButton
        backgroundColor="#0F107B"
        name="PRINT BARCODE"
        handleButtonClick={handleSubmit}
        disabled={selectedRows.length !== 1}
        test-id="SUBMIT"
      />
    </Grid>
  );
};

const PrintBarcodeModal = ({ handleClose, initialTab = 'quick' }) => {
  const themeMode = useSelector((state) => state?.theme?.themeMode);
  const isDark = themeMode === DARK_MODE;
  const { scaleValue } = useResolutionScale();
  const { showMessageOverlay } = useMessageContextManager();

  const [resetTrigger, setResetTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [scannedBarcodeValue, setScannedBarcodeValue] = useState('');
  const [scanDateTime, setScanDateTime] = useState(() => getDateTimeParts());
  const [customConfig, setCustomConfig] = useState(getCachedCustomPrintConfig);
  const [presetName, setPresetName] = useState('');
  const [selectedPresetName, setSelectedPresetName] = useState('');
  const [presets, setPresets] = useState(getCachedPrintPresets);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    localStorage.setItem(PRINT_LAYOUT_CACHE_KEY, JSON.stringify(customConfig));
  }, [customConfig]);
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  useEffect(() => {
    localStorage.setItem(PRINT_LAYOUT_PRESETS_KEY, JSON.stringify(presets));
  }, [presets]);

  const previewZpl = useMemo(
    () => buildCustomizedZpl(scannedBarcodeValue, customConfig, scanDateTime),
    [scannedBarcodeValue, customConfig, scanDateTime],
  );

  const onSubmit = (value, resetBarcodeValue) => {
    // UI-side implementation only: Directly print the scanned value
    try {
      // Create the ZPL properly using the template
      const zplString = formatZplTemplate(ZPL_TEMPLATE_FOR_BARCODE, value || '');

      const zplEncodedString = encodeURIComponent(zplString);
      window.location.href = `nirmaan://?cmd=print&zplstring=${zplEncodedString}`;

      showMessageOverlay({
        contextText: 'Printing Barcode...',
        success: true,
      });

      setScannedBarcodeValue(value || '');

      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      showMessageOverlay({
        contextText: 'Failed to trigger print',
        info: error.message,
        error: true,
      });
    }
  };

  const handleCustomConfigChange = (field, value) => {
    setCustomConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCustomPrint = () => {
    if (!scannedBarcodeValue?.trim()) {
      showMessageOverlay({
        contextText: 'Barcode required',
        info: 'Please scan or enter a barcode first.',
        error: true,
      });
      return;
    }

    try {
      const zplString = buildCustomizedZpl(
        scannedBarcodeValue.trim(),
        customConfig,
        scanDateTime,
      );
      const zplEncodedString = encodeURIComponent(zplString);
      window.location.href = `nirmaan://?cmd=print&zplstring=${zplEncodedString}`;
      showMessageOverlay({
        contextText: 'Custom print command sent',
        success: true,
      });
    } catch (error) {
      showMessageOverlay({
        contextText: 'Failed to print custom barcode',
        info: error.message,
        error: true,
      });
    }
  };

  const resetCustomConfig = () => {
    setCustomConfig(DEFAULT_CUSTOM_PRINT_CONFIG);
  };

  const savePreset = () => {
    if (!presetName.trim()) {
      showMessageOverlay({
        contextText: 'Preset name required',
        error: true,
      });
      return;
    }
    const next = [
      ...presets.filter((item) => item.name !== presetName.trim()),
      { name: presetName.trim(), config: customConfig },
    ];
    setPresets(next);
    setSelectedPresetName(presetName.trim());
    showMessageOverlay({ contextText: 'Preset saved', success: true });
  };

  const loadPreset = () => {
    const found = presets.find((item) => item.name === selectedPresetName);
    if (!found) return;
    setCustomConfig({
      ...DEFAULT_CUSTOM_PRINT_CONFIG,
      ...found.config,
    });
    showMessageOverlay({ contextText: 'Preset loaded', success: true });
  };

  const deletePreset = () => {
    if (!selectedPresetName) return;
    setPresets((prev) => prev.filter((item) => item.name !== selectedPresetName));
    setSelectedPresetName('');
    showMessageOverlay({ contextText: 'Preset deleted', success: true });
  };

  // Dimensions from ViewDocModal for consistency
  const borderRadius = scaleValue(12);
  // Keep operator UI compact: avoid large scaled padding gaps.
  const paddingY = scaleValue(22);
  const paddingX = scaleValue(16);
  const headerPaddingX = scaleValue(64);
  const modalWidth = '90%';
  const modalHeight = '90vh';
  const closeScale = scaleValue(2.0);

  return (
    <Modal
      open={true}
      onClose={handleClose}
      sx={{
        background: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'relative',
          background: isDark ? '#151A1E' : '#f2f2f2',
          borderRadius,
          width: modalWidth,
          height: modalHeight,
          overflow: 'hidden',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: `${paddingY}px ${paddingX}px`,
          color: isDark ? '#e6e6e6' : '#202020',
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            marginBottom: scaleValue(8),
            padding: `0 ${headerPaddingX}px`,
          }}
        >
          <Typography sx={{ fontSize: '30px', fontWeight: 600 }}>
            Barcode Printing
          </Typography>

          <IconButton
            onClick={handleClose}
            sx={{
              transform: `scale(${closeScale})`,
              color: isDark ? '#8a8a8a' : '#acacac',
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </Box>

        {/* BLUE AREA (Like View Document) */}
        <Box
          sx={{
            backgroundColor: '#ECF9FF',
            width: '96%',
            margin: '0 auto',
            borderRadius: '12px',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: { xs: '12px', md: '18px' },
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            gap: '10px',
            overflowY: 'auto'
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_e, value) => setActiveTab(value)}
            sx={{ width: '100%', maxWidth: '1000px', mb: 1 }}
          >
            <Tab value="quick" label="Quick Print" />
            <Tab value="custom" label="Custom Print" />
          </Tabs>

          <Box sx={{ width: '100%', maxWidth: '800px' }}>
            <Typography
              sx={{
                fontSize: '28px',
                fontWeight: 700,
                color: '#0F107B',
                mb: 2,
                textAlign: 'center',
              }}
            >
              Scan Barcode
            </Typography>

            <ScanOrEnterBarcodeForm
              placeholder="SCAN BARCODE"
              onSubmit={(value, resetBarcodeValue) => {
                const next = value || '';
                setScannedBarcodeValue(next);
                setScanDateTime(getDateTimeParts(new Date()));
                if (activeTab === 'quick') {
                  showMessageOverlay({
                    contextText: 'Barcode captured',
                    info: 'Preview updated. Click Print Now.',
                    success: true,
                  });
                } else {
                  showMessageOverlay({
                    contextText: 'Barcode captured',
                    info: 'Adjust layout and click Print in Custom Print tab.',
                    success: true,
                  });
                }
              }}
              enableAutoEnter
              resetTrigger={resetTrigger}
              isAlphaNumericValidation={false}
              textInputSx={{
                background: isDark ? '#1E1E1E' : '#fff',
                borderRadius: '12px',
                '& .MuiInputBase-input': {
                  fontSize: '24px',
                  padding: '16px 24px',
                  color: isDark ? '#fff' : '#0F107B',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(15, 16, 123, 0.5)',
                },
                '&.Mui-focused .MuiInputBase-input': {
                  // ScanOrEnterBarcodeForm switches background to white on focus.
                  // Force readable color so it doesn't become white-on-white.
                  color: '#0F107B',
                },
              }}
            />
          </Box>

          {activeTab === 'quick' ? (
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ width: '100%', maxWidth: '1200px' }}>
              <Paper sx={{ flex: 1, p: 2, borderRadius: 2 }}>
                <Typography sx={{ color: '#0F107B', fontSize: '22px', fontWeight: 800 }}>
                  Easy Mode
                </Typography>
                <Typography sx={{ color: '#0F107B', opacity: 0.7, mt: 1 }}>
                  1) Scan barcode
                </Typography>
                <Typography sx={{ color: '#0F107B', opacity: 0.7 }}>
                  2) Preview will update
                </Typography>
                <Typography sx={{ color: '#0F107B', opacity: 0.7, mb: 2 }}>
                  3) Click Print Now
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                  <Button variant="contained" onClick={handleCustomPrint} disabled={!scannedBarcodeValue?.trim()}>
                    Print Now
                  </Button>
                </Box>
              </Paper>

              <Paper sx={{ width: { xs: '100%', lg: 420 }, p: 2, borderRadius: 2 }}>
                <Typography sx={{ color: '#0F107B', fontSize: '22px', fontWeight: 800, mb: 1 }}>
                  Preview
                </Typography>
                <Typography sx={{ color: '#0F107B', opacity: 0.7, mb: 2 }}>
                  This is what will be printed.
                </Typography>

                <Box
                  sx={{
                    border: '2px solid #0F107B',
                    borderRadius: 1,
                    background: '#fff',
                    height: 155,
                    p: 1,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: '#333' }}>
                    Barcode: {scannedBarcodeValue || '---'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#333' }}>
                    Copies: {customConfig.copies} | {customConfig.arrangement}
                  </Typography>
                  <Box sx={{ mt: 1, border: '1px dashed #777', p: 1 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
                      Date / Time / Part / QR blocks follow layout settings.
                    </Typography>
                  </Box>
                </Box>

                <TextField
                  sx={{ mt: 2 }}
                  label="Technical ZPL Preview"
                  value={previewZpl}
                  multiline
                  minRows={4}
                  maxRows={9}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Paper>
            </Stack>
          ) : (
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ width: '100%', maxWidth: '1200px' }}>
              <Paper sx={{ flex: 1, p: 2, borderRadius: 2 }}>
                <Typography sx={{ color: '#0F107B', fontSize: '22px', fontWeight: 800 }}>
                  Step 1: Choose Simple Layout
                </Typography>
                <Typography sx={{ color: '#0F107B', opacity: 0.7, mb: 2 }}>
                  Keep this simple for shop-floor users.
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Chip
                    label="Single"
                    color={Number(customConfig.copies) === 1 ? 'primary' : 'default'}
                    onClick={() => handleCustomConfigChange('copies', 1)}
                  />
                  <Chip
                    label="2 in Row"
                    color={Number(customConfig.copies) === 2 && customConfig.arrangement === 'ROW' ? 'primary' : 'default'}
                    onClick={() => {
                      handleCustomConfigChange('copies', 2);
                      handleCustomConfigChange('arrangement', 'ROW');
                      handleCustomConfigChange('maxColumns', 2);
                    }}
                  />
                  <Chip
                    label="2 in Column"
                    color={Number(customConfig.copies) === 2 && customConfig.arrangement === 'COLUMN' ? 'primary' : 'default'}
                    onClick={() => {
                      handleCustomConfigChange('copies', 2);
                      handleCustomConfigChange('arrangement', 'COLUMN');
                      handleCustomConfigChange('maxRows', 2);
                    }}
                  />
                  <Chip
                    label="4 Grid"
                    color={Number(customConfig.copies) === 4 ? 'primary' : 'default'}
                    onClick={() => {
                      handleCustomConfigChange('copies', 4);
                      handleCustomConfigChange('arrangement', 'ROW');
                      handleCustomConfigChange('maxColumns', 2);
                      handleCustomConfigChange('maxRows', 2);
                    }}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
                  <TextField
                    label="Copies"
                    type="number"
                    value={customConfig.copies}
                    onChange={(e) => handleCustomConfigChange('copies', e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    select
                    label="Direction"
                    value={customConfig.arrangement}
                    onChange={(e) => handleCustomConfigChange('arrangement', e.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="ROW">Left to Right</MenuItem>
                    <MenuItem value="COLUMN">Top to Bottom</MenuItem>
                  </TextField>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Left Space"
                    type="number"
                    value={customConfig.leftMargin}
                    onChange={(e) => handleCustomConfigChange('leftMargin', e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Top Space"
                    type="number"
                    value={customConfig.topMargin}
                    onChange={(e) => handleCustomConfigChange('topMargin', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Stack>

                <Divider sx={{ my: 2 }} />
                <Typography sx={{ color: '#0F107B', fontWeight: 700, mb: 1 }}>
                  Presets (Save once, reuse anytime)
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                  <TextField
                    label="Preset Name"
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    size="small"
                  />
                  <Button variant="outlined" onClick={savePreset}>Save</Button>
                  <TextField
                    select
                    label="Saved Preset"
                    value={selectedPresetName}
                    onChange={(e) => setSelectedPresetName(e.target.value)}
                    size="small"
                    sx={{ minWidth: 180 }}
                  >
                    {presets.map((preset) => (
                      <MenuItem key={preset.name} value={preset.name}>{preset.name}</MenuItem>
                    ))}
                  </TextField>
                  <Button variant="outlined" onClick={loadPreset} disabled={!selectedPresetName}>Load</Button>
                  <Button variant="outlined" color="error" onClick={deletePreset} disabled={!selectedPresetName}>Delete</Button>
                </Stack>

                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={<Switch checked={showAdvanced} onChange={(e) => setShowAdvanced(e.target.checked)} />}
                  label="Show Advanced Settings"
                />

                {showAdvanced && (
                  <Box sx={{ mt: 1, border: '1px dashed #AFC8FF', borderRadius: 2, p: 2 }}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <TextField
                        select
                        label="Mode"
                        value={customConfig.mode}
                        onChange={(e) => handleCustomConfigChange('mode', e.target.value)}
                        size="small"
                        sx={{ minWidth: 170 }}
                      >
                        <MenuItem value="BUILDER">Visual Builder</MenuItem>
                        <MenuItem value="RAW_TEMPLATE">Raw ZPL</MenuItem>
                      </TextField>
                      <TextField label="Horizontal Gap" type="number" value={customConfig.horizontalGap} onChange={(e) => handleCustomConfigChange('horizontalGap', e.target.value)} size="small" />
                      <TextField label="Vertical Gap" type="number" value={customConfig.verticalGap} onChange={(e) => handleCustomConfigChange('verticalGap', e.target.value)} size="small" />
                      <TextField label="Label Width" type="number" value={customConfig.labelWidth} onChange={(e) => handleCustomConfigChange('labelWidth', e.target.value)} size="small" />
                      <TextField
                        select
                        label="Text Format"
                        value={customConfig.valueTransform}
                        onChange={(e) => handleCustomConfigChange('valueTransform', e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="NONE">Normal</MenuItem>
                        <MenuItem value="UPPER">UPPER</MenuItem>
                        <MenuItem value="LOWER">lower</MenuItem>
                      </TextField>
                      <TextField label="Text Prefix" value={customConfig.customTextPrefix} onChange={(e) => handleCustomConfigChange('customTextPrefix', e.target.value)} size="small" />
                    </Stack>

                    {customConfig.mode === 'RAW_TEMPLATE' && (
                      <TextField
                        sx={{ mt: 2 }}
                        label="Raw ZPL Template"
                        value={customConfig.rawTemplate}
                        onChange={(e) => handleCustomConfigChange('rawTemplate', e.target.value)}
                        multiline
                        minRows={6}
                        fullWidth
                        helperText="Placeholders: {PartNumber}, {Date}, {Time}, {VIN}"
                      />
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                  <Button variant="outlined" onClick={resetCustomConfig}>Reset</Button>
                  <Button variant="contained" onClick={handleCustomPrint}>Print Custom</Button>
                </Box>
              </Paper>

              <Paper sx={{ width: { xs: '100%', lg: 420 }, p: 2, borderRadius: 2 }}>
                <Typography sx={{ color: '#0F107B', fontSize: '22px', fontWeight: 800, mb: 1 }}>
                  Step 2: Preview
                </Typography>
                <Typography sx={{ color: '#0F107B', opacity: 0.7, mb: 2 }}>
                  Operator preview (what label roughly looks like)
                </Typography>

                <Box
                  sx={{
                    border: '2px solid #0F107B',
                    borderRadius: 1,
                    background: '#fff',
                    height: 175,
                    p: 1,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: '#333' }}>
                    Barcode: {scannedBarcodeValue || '---'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#333' }}>
                    Copies: {customConfig.copies} | {customConfig.arrangement}
                  </Typography>
                  <Box sx={{ mt: 1, border: '1px dashed #777', p: 1 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
                      Date / Time / Part / QR blocks will print as configured
                    </Typography>
                  </Box>
                </Box>

                <TextField
                  sx={{ mt: 2 }}
                  label="Technical ZPL Preview"
                  value={previewZpl}
                  multiline
                  minRows={4}
                  maxRows={9}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Paper>
            </Stack>
          )}
        </Box>

        {/* FOOTER / BACK BUTTON */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            padding: `0 ${scaleValue(32)}px`,
            marginTop: '16px',
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              backgroundColor: '#0F107B',
              color: '#fff',
              fontSize: '18px',
              fontWeight: 600,
              padding: '10px 32px',
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#0A0A5A' },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PrintBarcodeModal;
